import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Stack,
  Typography,
  Tooltip,
  LinearProgress,
  Snackbar
} from '@mui/material';
import {
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
  CloudDownload as CloudDownloadIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useTranslation } from 'react-i18next';

/**
 * Session Recovery Component
 * 
 * Session recovery component for recovering user session after crash with:
 * - Automatic session restoration
 * - User data recovery
 * - Continuity of user experience
 * - Session data management
 * - Recovery dialog
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onRestore - Callback when session is restored
 * @param {Function} props.onClear - Callback when session is cleared
 * @param {boolean} [props.autoRestore=false] - Whether to auto-restore session
 * @param {boolean} [props.showDialog=true] - Whether to show recovery dialog
 * @returns {JSX.Element} The session recovery component
 */
export const SessionRecovery = ({ 
  onRestore,
  onClear,
  autoRestore = false,
  showDialog = true,
  storageKeys = [],
  maxRecoveryAge = 24 * 60 * 60 * 1000 // 24 hours par default
}) => {
  const { t, i18n } = useTranslation();
  const [recoveryData, setRecoveryData] = useState([]);
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoredCount, setRestoredCount] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [lastCrashInfo, setLastCrashInfo] = useState(null);

  const sessionMetadataRef = useRef({
    lastActivity: Date.now(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString()
  });

  const stableMetadata = useMemo(() => sessionMetadataRef.current, []);

  const { manualSave: _saveSessionMetadata } = useAutoSave(
    stableMetadata,
    'session_metadata',
    {
      interval: 30000, // Save every 30 seconds
      storage: 'localStorage'
    }
  );

  // Detection of a potential crash
  const detectCrash = useCallback(() => {
    const lastSession = localStorage.getItem('session_active');
    const lastActivity = localStorage.getItem('session_last_activity');
    
    if (lastSession === 'true' && lastActivity) {
      const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
      const crashThreshold = 5 * 60 * 1000; // 5 minutes
      
      if (timeSinceLastActivity > crashThreshold) {
        return {
          detected: true,
          lastActivity: new Date(parseInt(lastActivity)),
          timeSinceLastActivity
        };
      }
    }
    
    return { detected: false };
  }, []);

  // Scan available recovery data
  const scanRecoveryData = useCallback(() => {
    const recoveryItems = [];
    const now = Date.now();
    
    // Scan localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      
      if (key && (key.startsWith('autosave_') || storageKeys.includes(key))) {
        try {
          const data = localStorage.getItem(key);
          const parsed = JSON.parse(data);
          
          // Check the age of the data
          const timestamp = parsed.timestamp ? new Date(parsed.timestamp).getTime() : 0;
          const age = now - timestamp;
          
          if (age <= maxRecoveryAge) {
            recoveryItems.push({
              key,
              data: parsed,
              timestamp: parsed.timestamp || '-',
              age,
              size: data.length,
              type: 'localStorage',
              description: getDataDescription(key, parsed)
            });
          }
        } catch (error) {
          void 0;
        }
      }
    }
    
    // Scan du sessionStorage
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      
      if (key && (key.startsWith('autosave_') || storageKeys.includes(key))) {
        try {
          const data = sessionStorage.getItem(key);
          const parsed = JSON.parse(data);
          
          const timestamp = parsed.timestamp ? new Date(parsed.timestamp).getTime() : 0;
          const age = now - timestamp;
          
          if (age <= maxRecoveryAge) {
            recoveryItems.push({
              key,
              data: parsed,
              timestamp: parsed.timestamp || '-',
              age,
              size: data.length,
              type: 'sessionStorage',
              description: getDataDescription(key, parsed)
            });
          }
        } catch (error) {
          void 0;
        }
      }
    }
    
    // Sort par timestamp (more recent en first)
    recoveryItems.sort((a, b) => {
      const timestampA = new Date(a.timestamp).getTime();
      const timestampB = new Date(b.timestamp).getTime();
      return timestampB - timestampA;
    });
    
    return recoveryItems;
  }, [storageKeys, maxRecoveryAge]);

  // Data description based on key
  const getDataDescription = (key, data) => {
    if (key.includes('form_')) return 'Form data';
    if (key.includes('order_')) return 'Order in progress';
    if (key.includes('inventory_')) return 'Inventory data';
    if (key.includes('settings_')) return 'User settings';
    if (key.includes('session_')) return 'Session data';
    if (data.data && typeof data.data === 'object') {
      const dataKeys = Object.keys(data.data);
      return `Data: ${dataKeys.slice(0, 3).join(', ')}${dataKeys.length > 3 ? '...' : ''}`;
    }
    return 'Data saved';
  };

  // Format the age of data
  const formatAge = (age) => {
    const minutes = Math.floor(age / (1000 * 60));
    const hours = Math.floor(age / (1000 * 60 * 60));
    const days = Math.floor(age / (1000 * 60 * 60 * 24));
    
    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}min`;
    return 'Now';
  };

  // Format the size of data
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Restore a specific item
  const restoreItem = useCallback(async (item) => {
    setIsRestoring(true);
    
    try {
      if (onRestore) {
        await onRestore(item.key, item.data, item);
      }
      
      setRestoredCount(prev => prev + 1);
      
      // Remove the item from the list after restoration
      setRecoveryData(prev => prev.filter(data => data.key !== item.key));
      
    } catch (error) {
      void 0;
    }finally {
      setIsRestoring(false);
    }
  }, [onRestore]);

  // Restore all items
  const restoreAll = useCallback(async () => {
    setIsRestoring(true);
    
    try {
      for (const item of recoveryData) {
        await restoreItem(item);
        // Use requestAnimationFrame to avoid blocking the interface
        await new Promise(resolve => requestAnimationFrame(resolve));
      }
      
      setShowSuccessMessage(true);
      setShowRecoveryDialog(false);
      
    } catch (error) {
      void 0;
    }finally {
      setIsRestoring(false);
    }
  }, [recoveryData, restoreItem]);

  // Delete a recovery item
  const deleteItem = useCallback((item) => {
    try {
      if (item.type === 'localStorage') {
        localStorage.removeItem(item.key);
      } else if (item.type === 'sessionStorage') {
        sessionStorage.removeItem(item.key);
      }
      
      setRecoveryData(prev => prev.filter(data => data.key !== item.key));
      
      if (onClear) {
        onClear(item.key, item);
      }
      
    } catch (error) {
      void 0;
    }
  }, [onClear]);

  // Delete all recovery data
  const clearAll = useCallback(() => {
    recoveryData.forEach(item => {
      try {
        if (item.type === 'localStorage') {
          localStorage.removeItem(item.key);
        } else if (item.type === 'sessionStorage') {
          sessionStorage.removeItem(item.key);
        }
      } catch (error) {
        void 0;
      }
    });
    
    setRecoveryData([]);
    setShowRecoveryDialog(false);
    
    if (onClear) {
      onClear('all', recoveryData);
    }
  }, [recoveryData, onClear]);

  // Refresh recovery data
  const refreshRecoveryData = useCallback(() => {
    const data = scanRecoveryData();
    setRecoveryData(data);
  }, [scanRecoveryData]);

  // Mark session as active
  const markSessionActive = useCallback(() => {
    localStorage.setItem('session_active', 'true');
    localStorage.setItem('session_last_activity', Date.now().toString());
  }, []);

  // Mark session as properly closed
  const markSessionClosed = useCallback(() => {
    localStorage.removeItem('session_active');
    localStorage.removeItem('session_last_activity');
  }, []);

  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const crashInfo = detectCrash();
    setLastCrashInfo(crashInfo);

    const data = scanRecoveryData();
    setRecoveryData(data);

    if (showDialog && (data.length > 0 || crashInfo.detected)) {
      setShowRecoveryDialog(true);
    }

    if (autoRestore && data.length > 0) {
      data.forEach(item => restoreItem(item));
    }

    markSessionActive();

    const handleBeforeUnload = () => {
      markSessionClosed();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      markSessionClosed();
    };
  }, [autoRestore, detectCrash, scanRecoveryData, showDialog, restoreItem, markSessionActive, markSessionClosed]);

  // Periodic activity update
  useEffect(() => {
    const interval = setInterval(() => {
      markSessionActive();
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, [markSessionActive]);

  return (
    <>
      {/* Session recovery dialog */}
      <Dialog 
        open={showRecoveryDialog} 
        onClose={() => setShowRecoveryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <RestoreIcon color="primary" />
            <Typography variant="h6">
              {t('session.recoveryTitle', 'Session Recovery')}
            </Typography>
          </Stack>
        </DialogTitle>
        
        <DialogContent>
          <Stack spacing={3}>
            {/* Detected crash information */}
            {lastCrashInfo?.detected && (
              <Alert severity="warning">
                <AlertTitle>{t('session.interruptedSession', 'Interrupted session detected')}</AlertTitle>
                <Typography variant="body2">
                  {t('session.crashDescription', 'The application seems to have closed unexpectedly on')}{' '}
                  {lastCrashInfo.lastActivity.toLocaleString(i18n?.language || 'en-US')}.
                  {t('session.dataRecoverable', 'We found data that can be recovered.')}
                </Typography>
              </Alert>
            )}
            
            {/* Recovery data list */}
            {recoveryData.length > 0 ? (
              <>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1">
                      {t('session.availableData', 'Available data for retrieval')} ({recoveryData.length})
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={refreshRecoveryData}
                    >
                      {t('common.refresh', 'Refresh')}
                    </Button>
                  </Stack>
                  
                  <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <List>
                      {recoveryData.map((item, index) => (
                        <React.Fragment key={item.key}>
                          <ListItem>
                            <ListItemIcon>
                              <StorageIcon color="primary" />
                            </ListItemIcon>
                            
                            <ListItemText
                              primary={item.description}
                              secondary={
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Chip 
                                    label={formatAge(item.age)} 
                                    size="small" 
                                    icon={<ScheduleIcon />}
                                  />
                                  <Chip 
                                    label={formatSize(item.size)} 
                                    size="small" 
                                    variant="outlined"
                                  />
                                  <Chip 
                                    label={item.type} 
                                    size="small" 
                                    color="secondary"
                                  />
                                </Stack>
                              }
                            />
                            
                            <ListItemSecondaryAction>
                              <Stack direction="row" spacing={1}>
                                <Tooltip title={t('session.restoreItem', 'Restore this item')}>
                                  <IconButton
                                    edge="end"
                                    onClick={() => restoreItem(item)}
                                    disabled={isRestoring}
                                  >
                                    <RestoreIcon />
                                  </IconButton>
                                </Tooltip>
                                
                                <Tooltip title={t('session.deleteItem', 'Delete this item')}>
                                  <IconButton
                                    edge="end"
                                    onClick={() => deleteItem(item)}
                                    disabled={isRestoring}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </ListItemSecondaryAction>
                          </ListItem>
                          
                          {index < recoveryData.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Paper>
                </Box>
                
                {/* Progress bar */}
                {isRestoring && (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      {t('session.restorationInProgress', 'Restoration in progress...')}
                    </Typography>
                    <LinearProgress />
                  </Box>
                )}
              </>
            ) : (
              <Alert severity="info">
                <AlertTitle>No data available</AlertTitle>
                <Typography variant="body2">
                  {t('session.noDataFound', 'No recovery data found or all available data is too old.')}
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowRecoveryDialog(false)}>
            {t('common.ignore', 'Ignore')}
          </Button>
          
          {recoveryData.length > 0 && (
            <>
              <Button 
                onClick={clearAll}
                color="error"
                disabled={isRestoring}
              >
                {t('common.deleteAll', 'Delete all')}
              </Button>
              
              <Button 
                onClick={restoreAll}
                variant="contained"
                startIcon={<RestoreIcon />}
                disabled={isRestoring}
              >
                {t('session.restoreAll', 'Restore all')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Success message */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setShowSuccessMessage(false)}
      >
        <Alert 
          onClose={() => setShowSuccessMessage(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          {t('session.restoreSuccess', '{{count}} element(s) restored successfully!', { count: restoredCount })}
        </Alert>
      </Snackbar>
    </>
  );
};


export default React.memo(SessionRecovery);

